import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import toast from "react-hot-toast";
import { getTestimonialsApi, createTestimonialsApi, updateTestimonialsApi, deleteTestimonialsApi } from "../../helper/apiHelper";

const TestimonialsComp = () => {
    const [testimonials, setTestimonials] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [testimonialToDelete, setTestimonialToDelete] = useState(null);
    const [newTestimonial, setNewTestimonial] = useState({
        id: '',
        name: '',
        designation: '',
        rating: 5,
        message: ''
    });

    // Fetch testimonials on mount
    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        try {
            setIsLoading(true);
            const response = await getTestimonialsApi();
            setTestimonials(response.data || []);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch testimonials"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTestimonial({ ...newTestimonial, [name]: value });
    };

    const handleSave = async () => {
        if (!newTestimonial.name || !newTestimonial.message) {
            toast.error("Name and Message are required");
            return;
        }

        if (!newTestimonial.designation) {
            toast.error("Designation is required");
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                name: newTestimonial.name,
                designation: newTestimonial.designation,
                rating: newTestimonial.rating.toString(),
                message: newTestimonial.message
            };

            if (isEditing) {
                await updateTestimonialsApi(payload, newTestimonial.id);
                toast.success("Testimonial updated successfully");
            } else {
                await createTestimonialsApi(payload);
                toast.success("Testimonial added successfully");
            }

            setShowModal(false);
            setNewTestimonial({ id: '', name: '', designation: '', rating: 5, message: '' });
            setIsEditing(false);
            fetchTestimonials(); // Refresh the list
        } catch (error) {
            toast.error(error || "Failed to save testimonial");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (item) => {
        setIsEditing(true);
        setNewTestimonial(item);
        setShowModal(true);
    };

    const handleDelete = (item) => {
        setTestimonialToDelete(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!testimonialToDelete) return;

        try {
            setIsLoading(true);
            await deleteTestimonialsApi(testimonialToDelete.id);
            toast.success("Testimonial deleted successfully");
            setShowDeleteModal(false);
            setTestimonialToDelete(null);
            fetchTestimonials(); // Refresh the list
        } catch (error) {
            toast.error(error || "Failed to delete testimonial");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            {/* Header Section */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-24">
                <h6 className="fw-semibold mb-0">Testimonials</h6>
                <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    onClick={() => {
                        setIsEditing(false);
                        setNewTestimonial({ id: '', name: '', designation: '', rating: 5, message: '' });
                        setShowModal(true);
                    }}
                >
                    <Icon icon="ic:baseline-plus" className="text-xl" />
                    Add Testimonial
                </button>
            </div>

            {/* Content Section */}
            {isLoading ? (
                <div className="text-center py-40">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            ) : testimonials.length === 0 ? (
                <div className="text-center py-40 text-muted fw-medium" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No testimonials found.
                </div>
            ) : (
                <div className='row gy-4'>
                    {testimonials.map((item) => (
                        <div className='col-xxl-4 col-md-6' key={item.id}>
                            <div className='card rounded-3 overflow-hidden h-100'>
                                <div className='card-body p-24'>
                                    <div className='d-flex align-items-start justify-content-between gap-2'>
                                        <div className='d-flex align-items-center gap-12 flex-wrap'>
                                            <div className='d-flex flex-column flex-grow-1'>
                                                <h6 className='text-xl mb-0'>{item.name}</h6>
                                                <span className='text-neutral-500'>{item.designation}</span>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column align-items-end gap-2">
                                            <ul className='d-flex align-items-center justify-content-end gap-1'>
                                                {[...Array(5)].map((_, i) => (
                                                    <li key={i} className={`${i < item.rating ? 'text-warning-600' : 'text-neutral-300'} text-xl d-flex line-height-1`}>
                                                        <Icon icon="ri:star-fill" />
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="d-flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-primary-50 text-primary-600 hover-bg-primary-100"
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item)}
                                                    className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-danger-50 text-danger-600 hover-bg-danger-100"
                                                >
                                                    <Icon icon="fluent:delete-24-regular" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <p className='mt-24 text-neutral-500 text-lg mb-0'>
                                        {item.message}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">{isEditing ? "Edit Testimonial" : "Add Testimonial"}</h1>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <form className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={newTestimonial.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter Name"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Designation</label>
                                            <input
                                                type="text"
                                                name="designation"
                                                className="form-control"
                                                value={newTestimonial.designation}
                                                onChange={handleInputChange}
                                                placeholder="Enter Designation"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Rating</label>
                                            <select
                                                name="rating"
                                                className="form-select"
                                                value={newTestimonial.rating}
                                                onChange={handleInputChange}
                                            >
                                                <option value="1">1 Star</option>
                                                <option value="2">2 Stars</option>
                                                <option value="3">3 Stars</option>
                                                <option value="4">4 Stars</option>
                                                <option value="5">5 Stars</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Message</label>
                                            <textarea
                                                name="message"
                                                className="form-control"
                                                rows="4"
                                                value={newTestimonial.message}
                                                onChange={handleInputChange}
                                                placeholder="Enter Message"
                                            />
                                        </div>
                                        <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                            <button
                                                type="button"
                                                className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                                onClick={() => setShowModal(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary border border-primary-600 text-md px-32 py-10 radius-8"
                                                onClick={handleSave}
                                                disabled={isLoading}
                                            >
                                                {isEditing ? 'Update' : 'Save'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">Confirm Delete</h1>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setTestimonialToDelete(null);
                                        }}
                                    />
                                </div>

                                <div className="modal-body p-24">
                                    <p className="text-secondary-light mb-0">
                                        Are you sure you want to delete the testimonial from <strong>"{testimonialToDelete?.name}"</strong>? This action cannot be undone.
                                    </p>
                                </div>

                                <div className="modal-footer py-16 px-24 border-top">
                                    <button
                                        type="button"
                                        className="border border-neutral-300 bg-hover-neutral-200 text-secondary-light text-md px-24 py-10 radius-8"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setTestimonialToDelete(null);
                                        }}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-danger border border-danger-600 text-md px-32 py-10 radius-8 d-flex align-items-center gap-2"
                                        onClick={confirmDelete}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                                Deleting...
                                            </>
                                        ) : (
                                            'Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}
        </div>
    );
};

export default TestimonialsComp;
